/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';
import {
	defaultEmojiHeight,
	type EmojiId,
	type OnEmojiEvent,
	type EmojiProvider,
	ResourcedEmoji,
} from '@atlaskit/emoji';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { messages } from '../shared/i18n';
import { isLeftClick } from '../shared/utils';
import { RESOURCED_EMOJI_COMPACT_HEIGHT } from '../shared/constants';
import { Pressable } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	emojiButton: {
		outline: 'none',
		display: 'flex',
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: '0',
		borderRadius: token('radius.small'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),

		'&:hover': {
			transition: 'transform cubic-bezier(0.23, 1, 0.32, 1) 200ms',
			transform: 'scale(1.33)',
		},
	},

	hoverableReactionPickerSelectorEmojiButton: {
		outline: 'none',
		display: 'flex',
		transformOrigin: 'center center 0',
		paddingTop: token('space.025'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	emojiSlot: {
		minWidth: '20px',
	},

	hoverableReactionPickerSelectorEmojiSlot: {
		minWidth: '16px',
	},
});

export const RENDER_BUTTON_TESTID = 'button-emoji-id';
export const RENDER_BUTTON_EMOJI_SLOT_TESTID = 'button-emoji-slot-id';

export interface EmojiButtonProps {
	/**
	 * identifier info for a given emoji
	 */
	emojiId: EmojiId;
	/**
	 * Async provider to fetch the emoji
	 */
	emojiProvider: Promise<EmojiProvider>;
	/**
	 * Optional prop for hoverable reaction picker selector emoji
	 */
	hoverableReactionPickerSelectorEmoji?: boolean;
	/**
	 * Event handler when a new emoji is selected
	 */
	onClick: OnEmojiEvent;
}

/**
 * custom button to render the custom emoji selector inside the reaction picker
 */
export const EmojiButton = ({
	emojiId,
	onClick,
	emojiProvider,
	hoverableReactionPickerSelectorEmoji,
}: EmojiButtonProps): JSX.Element => {
	const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (onClick && isLeftClick(event)) {
			onClick(emojiId, undefined, event);
		}
	};

	const intl = useIntl();
	const emojiHeight = hoverableReactionPickerSelectorEmoji
		? RESOURCED_EMOJI_COMPACT_HEIGHT
		: defaultEmojiHeight;
	const emoji = (
		<ResourcedEmoji
			emojiProvider={emojiProvider}
			emojiId={emojiId}
			fitToHeight={
				hoverableReactionPickerSelectorEmoji ? RESOURCED_EMOJI_COMPACT_HEIGHT : undefined
			}
		/>
	);
	const reservedEmoji = hoverableReactionPickerSelectorEmoji ? (
		<span
			css={styles.hoverableReactionPickerSelectorEmojiSlot}
			data-testid={RENDER_BUTTON_EMOJI_SLOT_TESTID}
		>
			<ResourcedEmoji emojiProvider={emojiProvider} emojiId={emojiId} fitToHeight={emojiHeight} />
		</span>
	) : (
		<span css={styles.emojiSlot} data-testid={RENDER_BUTTON_EMOJI_SLOT_TESTID}>
			<ResourcedEmoji emojiProvider={emojiProvider} emojiId={emojiId} fitToHeight={emojiHeight} />
		</span>
	);

	return (
		<Pressable
			testId={RENDER_BUTTON_TESTID}
			onClick={onButtonClick}
			aria-label={intl.formatMessage(messages.reactWithEmoji, {
				emoji: emojiId.shortName,
			})}
			xcss={
				hoverableReactionPickerSelectorEmoji
					? styles.hoverableReactionPickerSelectorEmojiButton
					: styles.emojiButton
			}
		>
			{/* eslint-disable-next-line @atlaskit/platform/use-recommended-utils */}
			{FeatureGates.getExperimentValue(
				'platform_teamoji_26_refresh_emoji_picker',
				'isEnabled',
				false,
			)
				? reservedEmoji
				: emoji}
		</Pressable>
	);
};
